<script setup lang="ts">
import { computed } from 'vue';
import type { NyxClient, NyxMode, NyxStatus } from '@atum-remote/nyx-link-protocol';
import {
  clientDescription,
  clientLabel,
  modeDescription,
  modeLabel,
} from '../lib/nyx-presentation.js';
import AppIcon from './AppIcon.vue';
import type { IconName } from './icons.js';
import { nyxMessageError } from '../lib/nyx-actions.js';

/**
 * The start screen of a task: pick where it runs, which harness drives it,
 * how much it may do on its own, and optionally what to say first.
 */
const props = defineProps<{
  deviceName: string;
  projects: readonly { projectKey: string; name: string; mapped: boolean }[];
  projectsTruncated: boolean;
  clients: NyxStatus['clients'];
  modes: readonly NyxMode[];
  models: readonly { id: string; name: string; efforts: readonly string[] }[];
  efforts: readonly string[];
  busy: boolean;
  canStart: boolean;
}>();
const emit = defineEmits<{ start: []; clientChange: [] }>();
const project = defineModel<string>('project', { required: true });
const client = defineModel<NyxClient>('client', { required: true });
const mode = defineModel<NyxMode>('mode', { required: true });
const model = defineModel<string>('model', { required: true });
const effort = defineModel<string>('effort', { required: true });
const prompt = defineModel<string>('prompt', { required: true });

const modeIcon: Readonly<Record<NyxMode, IconName>> = {
  ask_me: 'question',
  plan_only: 'eye',
  auto_approve: 'bolt',
};
const chosenProject = computed(() =>
  props.projects.find((entry) => entry.projectKey === project.value),
);
const installed = computed(() => props.clients.filter((entry) => entry.installed).length);
const promptError = computed(() => nyxMessageError(prompt.value, true));
</script>

<template>
  <section class="nyx-new">
    <header class="nyx-new-head">
      <span class="section-icon" aria-hidden="true"><AppIcon name="plus" /></span>
      <div>
        <h2>New task</h2>
        <p>
          Start a hosted coding session in a repository linked on {{ deviceName }}. It keeps running
          there when you close this view.
        </p>
      </div>
    </header>

    <form class="nyx-new-form" @submit.prevent="emit('start')">
      <div class="field">
        <span class="field-label">Project</span>
        <span class="select-field">
          <select v-model="project" aria-label="Project" :disabled="busy || !projects.length">
            <option v-if="!projects.length" value="">No projects mapped in Nyx Link</option>
            <option
              v-for="entry in projects"
              :key="entry.projectKey"
              :value="entry.projectKey"
              :disabled="!entry.mapped"
            >
              {{ entry.name }}{{ entry.mapped ? '' : ' · not linked to a worktree' }}
            </option>
          </select>
          <AppIcon name="chevronDown" />
        </span>
        <span v-if="chosenProject && !chosenProject.mapped" class="field-help">
          Map this project to a Git worktree in Nyx Link before starting a task here.
        </span>
        <span v-else-if="projectsTruncated" class="field-help">
          Only the first projects are listed. Link or inspect others directly in Nyx Link.
        </span>
      </div>

      <div class="field">
        <span class="field-label">Harness</span>
        <div class="nyx-harness" role="radiogroup" aria-label="Harness">
          <label
            v-for="entry in clients"
            :key="entry.client"
            class="nyx-harness-option"
            :data-installed="entry.installed ? 'true' : 'false'"
          >
            <input
              v-model="client"
              type="radio"
              name="nyx-harness"
              :value="entry.client"
              :disabled="busy || !entry.installed"
              @change="emit('clientChange')"
            />
            <span class="nyx-harness-name">{{ clientLabel[entry.client] }}</span>
            <span class="nyx-harness-note">{{
              entry.installed ? clientDescription[entry.client] : 'Not installed on this host'
            }}</span>
          </label>
        </div>
        <span v-if="!installed" class="field-help">
          No coding CLI is installed and authenticated on {{ deviceName }}.
        </span>
      </div>

      <div class="field">
        <span class="field-label">Mode</span>
        <div class="segmented nyx-modes" role="radiogroup" aria-label="Mode">
          <label v-for="entry in modes" :key="entry">
            <input v-model="mode" type="radio" name="nyx-mode" :value="entry" :disabled="busy" />
            <AppIcon :name="modeIcon[entry]" />{{ modeLabel[entry] }}
          </label>
        </div>
        <span class="field-help">{{ modeDescription[mode] }}</span>
      </div>

      <div class="nyx-new-row">
        <div class="field">
          <span class="field-label">Model</span>
          <span class="select-field">
            <select v-model="model" aria-label="Model" :disabled="busy">
              <option value="">Client default</option>
              <option v-for="entry in models" :key="entry.id" :value="entry.id">
                {{ entry.name }}
              </option>
            </select>
            <AppIcon name="chevronDown" />
          </span>
        </div>
        <div v-if="efforts.length" class="field">
          <span class="field-label">Reasoning effort</span>
          <span class="select-field">
            <select v-model="effort" aria-label="Reasoning effort" :disabled="busy">
              <option value="">Client default</option>
              <option v-for="entry in efforts" :key="entry" :value="entry">{{ entry }}</option>
            </select>
            <AppIcon name="chevronDown" />
          </span>
        </div>
      </div>

      <div v-if="mode === 'auto_approve'" class="callout" data-tone="warn">
        <AppIcon name="bolt" />
        <div>
          <strong>Auto-approve acts without asking</strong>
          <p>
            The client runs commands and changes files on its own, within the permissions of your
            installed client on {{ deviceName }}.
          </p>
        </div>
      </div>
      <div v-else-if="client === 'claude'" class="callout">
        <AppIcon name="info" />
        <div>
          <strong>Claude Code approvals stay on the host</strong>
          <p>
            Its streaming interface cannot route interactive approvals here, so Plan only and
            Auto-approve are the available modes.
          </p>
        </div>
      </div>

      <div class="field">
        <span class="field-label">First message <span class="muted">(optional)</span></span>
        <textarea
          v-model="prompt"
          class="nyx-new-prompt"
          rows="4"
          maxlength="16000"
          :disabled="busy"
          placeholder="What should the client work on?"
        ></textarea>
        <span v-if="promptError" class="field-help" role="status">{{ promptError }}</span>
      </div>

      <div class="nyx-new-actions">
        <button type="submit" class="primary" :disabled="!canStart">
          <AppIcon name="bolt" />{{ prompt.trim() ? 'Start and send' : 'Start task' }}
        </button>
        <span class="hint">
          Runs under your installed client's own account, configuration and MCP servers.
        </span>
      </div>
    </form>
  </section>
</template>
